<template>
  <!-- Toast Messages Container -->
  <Teleport to="body">
    <TransitionGroup name="toast-list" tag="div" class="toast-container">
      <ToastMessage
        v-for="(toast, index) in toasts"
        :key="toast.id"
        :type="toast.type"
        :title="toast.title"
        :message="toast.message"
        :duration="toast.duration"
        :index="index"
        @close="removeToast(toast.id)"
      />
    </TransitionGroup>
  </Teleport>

  <!-- Confirm Modal -->
  <ConfirmModal
    v-model="confirmState.visible"
    :title="confirmState.title"
    :message="confirmState.message"
    :confirm-text="confirmState.confirmText"
    :cancel-text="confirmState.cancelText"
    :type="confirmState.type"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />

  <!-- Prompt Modal -->
  <PromptModal
    v-model="promptState.visible"
    :title="promptState.title"
    :message="promptState.message"
    :default-value="promptState.defaultValue"
    :confirm-text="promptState.confirmText"
    :cancel-text="promptState.cancelText"
    @confirm="handlePromptConfirm"
    @cancel="handlePromptCancel"
  />

  <!-- 额度不足弹窗（全局，所有 AI 功能共用） -->
  <div v-if="quotaError.visible" class="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" @click.self="closeQuotaError">
    <div class="bg-white dark:bg-[#131B2C] rounded-2xl w-[400px] shadow-2xl p-8 text-center">
      <div class="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/></svg>
      </div>
      <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">AI 调用额度不足</h3>
      <p class="text-sm text-slate-500 mb-6">{{ quotaError.message }}</p>
      <button @click="closeQuotaError" class="px-6 py-2.5 text-sm font-medium text-white bg-[#2F6BFF] rounded-lg hover:opacity-90">知道了</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useToast } from '@/composables/useToast'
import { useConfirm } from '@/composables/useConfirm'
import { usePrompt } from '@/composables/usePrompt'
import ToastMessage from './ToastMessage.vue'
import ConfirmModal from './ConfirmModal.vue'
import PromptModal from './PromptModal.vue'

const { toasts, remove: removeToast } = useToast()
const { confirmState, confirm: handleConfirm, cancel: handleCancel } = useConfirm()
const { promptState, confirm: handlePromptConfirm, cancel: handlePromptCancel } = usePrompt()

// 额度不足全局弹窗（由 axios 拦截器触发，覆盖所有 AI 功能）
const quotaError = ref({ visible: false, message: 'AI 调用额度不足：次数已用完' })
function showQuotaError(message: string) {
  quotaError.value = { visible: true, message: message || 'AI 调用额度不足：次数已用完' }
}
const closeQuotaError = () => { quotaError.value.visible = false }

onMounted(() => {
  ;(window as any).showQuotaError = showQuotaError
})
onUnmounted(() => {
  delete (window as any).showQuotaError
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10000;
  pointer-events: none;
}

.toast-container > * {
  pointer-events: auto;
}

.toast-list-move {
  transition: all 0.3s ease;
}

.toast-list-enter-active,
.toast-list-leave-active {
  transition: all 0.3s ease;
}

.toast-list-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
